import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Building2, PoundSterling, TrendingUp, ShieldCheck, Users,
  Loader2, Calendar, ExternalLink, Car,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** /group/dealers/:workshopId — read-only drill-down into one dealer. */
export default function GroupDealerDrilldownPage() {
  const { workshopId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workshopId) return;
    api.get(`/group/dealers/${workshopId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Couldn't load dealer");
        nav("/group");
      })
      .finally(() => setLoading(false));
  }, [workshopId, nav]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { workshop, period, kpis, recent_jobs } = data;
  const periodLabel = new Date(period.from + "T00:00:00Z").toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/group" className="btn-outline" data-testid="back-to-group-btn">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Group</span>
          </Link>
          <div className="min-w-0 text-right">
            <div className="label-overline text-primary">Dealer drilldown</div>
            <div className="font-display font-bold text-lg truncate">{workshop.name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="period-strip">
          <Calendar className="w-4 h-4" /> Period: <strong className="text-foreground">{periodLabel}</strong>
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-muted/50 border border-border px-1.5 py-0.5 rounded">
            <ShieldCheck className="w-3 h-3" /> Read-only
          </span>
        </div>

        {/* KPI tiles */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dealer-kpi-tiles">
          <Kpi label="Approved value" value={`£${kpis.approved_total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`} sub={`${kpis.job_count} jobs`} icon={<PoundSterling className="w-5 h-5" />} accent="primary" />
          <Kpi label="Conversion" value={`${kpis.conversion_pct}%`} sub={`${kpis.sent_count} sent`} icon={<TrendingUp className="w-5 h-5" />} accent="success" />
          <Kpi label="Red items recovered" value={kpis.red_recovered} sub="Safety work agreed" icon={<ShieldCheck className="w-5 h-5" />} accent="warning" />
          <Kpi label="Plans sold" value={kpis.plans_sold} sub="Via AI pitch loop" icon={<Users className="w-5 h-5" />} accent="info" />
        </section>

        {/* Recent jobs */}
        <section data-testid="recent-jobs-section">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-3">Recent authorised work</h2>
          {recent_jobs.length === 0 ? (
            <div className="card-default p-8 text-center text-muted-foreground" data-testid="no-recent-jobs">
              No customer-authorised work in this period.
            </div>
          ) : (
            <div className="card-default overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Customer</th>
                      <th className="px-4 py-3 text-left font-semibold hidden sm:table-cell">Vehicle</th>
                      <th className="px-4 py-3 text-left font-semibold hidden md:table-cell">Responded</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-right font-semibold">Approved £</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {recent_jobs.map((j) => (
                      <tr key={j.id} className="hover:bg-muted/30" data-testid={`drilldown-job-${j.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold">{j.customer_name || "—"}</div>
                          <div className="text-xs text-muted-foreground sm:hidden">{j.vehicle} · {j.vehicle_reg}</div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <div className="flex items-center gap-1.5">
                            <Car className="w-3.5 h-3.5 text-muted-foreground" />
                            <span>{j.vehicle}</span>
                            {j.vehicle_reg && <span className="font-mono-plex text-xs text-muted-foreground">· {j.vehicle_reg}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                          {j.responded_at ? new Date(j.responded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                            j.status === "completed" ? "bg-success/10 border-success/30 text-success" :
                            j.status === "approved" ? "bg-primary/10 border-primary/30 text-primary" :
                            "bg-warning/10 border-warning/30 text-warning"
                          }`}>{j.status}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono-plex font-semibold">
                          £{j.approved_total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-border bg-muted/30 p-4 text-xs text-muted-foreground flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong className="text-foreground">Read-only.</strong> This view shows aggregated performance for <strong>{workshop.name}</strong>. To open or edit individual jobs, contact the dealership manager.
          </div>
        </section>
      </main>
    </div>
  );
}

function Kpi({ label, value, sub, icon, accent = "primary" }) {
  const accentBg = {
    primary: "bg-primary/10 text-primary",
    success: "bg-success/10 text-success",
    warning: "bg-warning/10 text-warning",
    info: "bg-info/10 text-info",
  }[accent];
  return (
    <div className="card-default p-5">
      <div className="flex items-start justify-between">
        <div className="label-overline text-muted-foreground">{label}</div>
        <div className={`w-8 h-8 rounded-md grid place-items-center ${accentBg}`}>{icon}</div>
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-2">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
